'use client';

import { type FC, useEffect, useState } from 'react';
import { ExclusivePostLaunchBackground } from '@/app/components/game/layouts/exclusive-post-launch/subcomponents/background';
import { ExclusivePostLaunchTrailer } from '@/app/components/game/layouts/exclusive-post-launch/subcomponents/trailer';
import { ExclusivePostLaunchGameDetails } from '@/app/components/game/layouts/exclusive-post-launch/subcomponents/game-details';
import { classes } from '@/utils/classes/classes';
import type { ExclusiveZone } from '@/types/zones/exclusive-zone';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import './styles/default.scss';
import { useExclusivePopupTrigger } from '@/hooks/use-exclusive-popup-trigger';
import { getExclusiveProps } from '@/logic/exclusive-launch';
import { validatePostLaunchContent } from '@/utils/exclusive-popup/validate-content';
import { EXCLUSIVE_ZONE_NAMES, SLICE_TYPE, EXCLUSIVE_LAUNCH_TYPE } from '@/utils/constants';
import BadgeFactoryClient from '@/app/components/badge/badge-factory.client';
import { TextBadge } from '@/app/components/badge/layouts/text/text-badge';
import { getText } from '@/utils/text/text';

type ExclusivePostProps = {
    zone: ExclusiveZone;
    isMobileDevice: boolean;
};

export const ExclusivePostLaunch: FC<ExclusivePostProps> = ({ zone, isMobileDevice }) => {
    const validated = validatePostLaunchContent(zone);
    const { layoutContent, trailerProps, gameDetailsProps, logoUrl, copyrightText } = getExclusiveProps(
        zone,
        isMobileDevice
    );
    const { gameUid, gameName } = gameDetailsProps.launchParameters;
    const [isLandscape, setIsLandscape] = useState(!isMobileDevice);
    const [isTrailerReady, setIsTrailerReady] = useState(false);
    const orientationClass = isLandscape ? 'landscape' : 'portrait';
    const { featureFlags } = useGlobalContext();
    const isEarlyAccess = featureFlags[PRODUCT_THROTTLES.HAS_EARLY_ACCESS];
    const zoneContent = zone?.data;
    const earlyAccessZone = zoneContent?.exclusive_launch_type === EXCLUSIVE_LAUNCH_TYPE.EARLY_ACCESS;

    useExclusivePopupTrigger({
        zoneData: zoneContent,
        zoneUid: zone?.uid ?? '',
        isMobile: isMobileDevice,
        exclusiveType: SLICE_TYPE.EXCLUSIVE_POST_LAUNCH,
        exclusiveLaunchType: zoneContent?.exclusive_launch_type || ''
    });

    const handleWindowSizeChange = () => {
        if (isMobileDevice) {
            setIsLandscape(false);
        } else {
            setIsLandscape(window.innerWidth >= 740);
        }
    };

    useEffect(() => {
        handleWindowSizeChange();
        setIsTrailerReady(true);
        window.addEventListener('resize', handleWindowSizeChange);

        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        };
    }, []);

    if (!validated) {
        return null;
    }

    return (
        <div
            className={classes(
                'exclusive-post-launch',
                orientationClass,
                isEarlyAccess && 'early-access',
                earlyAccessZone && 'early-access-zone'
            )}
            id="exclusive-post-launch"
            data-t-module="post_launch_pop_up"
            data-t-prepostlaunch="post-launch"
            data-t-zonename={
                earlyAccessZone ? EXCLUSIVE_ZONE_NAMES.POST_LAUNCH_EARLY_ACCESS : EXCLUSIVE_ZONE_NAMES.POST_LAUNCH
            }
            data-t-zonelayout={EXCLUSIVE_ZONE_NAMES.POST_LAUNCH}
            data-t-gameuid={gameUid}
            data-t-gamename={gameName}
            data-t-gameposition={1}>
            {earlyAccessZone && isLandscape && (
                <BadgeFactoryClient>
                    <TextBadge label={getText({ text: 'components.badge.earlyAccess' })} />
                </BadgeFactoryClient>
            )}
            <div className="content">
                <ExclusivePostLaunchBackground layoutContent={layoutContent} isLandscape={isLandscape} />
                <div className="inner">
                    <ExclusivePostLaunchTrailer
                        layoutContent={layoutContent}
                        trailerProps={trailerProps}
                        isLandscape={isLandscape}
                        exclusiveType={zoneContent.exclusive_launch_type}
                        isTrailerReady={isTrailerReady}
                    />
                    <ExclusivePostLaunchGameDetails
                        layoutContent={layoutContent}
                        gameDetailsProps={gameDetailsProps}
                        isLandscape={isLandscape}
                    />
                </div>
                {!isLandscape && logoUrl && <img src={logoUrl} alt="Exclusive Game Logo" className="game-logo" />}
                {isLandscape && (
                    <div className="copyright" data-testid="exclusive-copyright">
                        {copyrightText}
                    </div>
                )}
            </div>
        </div>
    );
};
